import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { tenantSettingsAPI, templateAPI, clientDocumentAPI, staffAPI } from '../services/api';
import ContentHeader from '../components/ContentHeader';
import './TenantSettings.css';

/** Flatten folder tree to { key, label } for dropdowns. */
function flattenFolderOptions(folders, prefix = '') {
  if (!folders || !folders.length) return [];
  const out = [];
  for (const f of folders) {
    const label = prefix ? `${prefix} → ${f.name}` : (f.name || f.key || '');
    const key = (f.key && String(f.key).trim()) || (f.name && f.name.replace(/\s+/g, '-').toLowerCase()) || '';
    if (key) out.push({ key, label });
    if (f.children?.length) out.push(...flattenFolderOptions(f.children, f.name || f.key));
  }
  return out;
}

/** Only buffer days are editable; legal values are government-driven and read-only. */
const BUFFER_ROWS = [
  { key: 'initial_ifsp_buffer_days', label: 'Initial IFSP', legalLabel: (cfg) => `legal = ${cfg?.initialIfspLegalDays ?? '—'} days from open` },
  { key: 'tpm_tpc_buffer_days', label: 'TPM/TPC', legalLabel: (cfg) => `legal = age 3 − ${cfg?.tpmTpcLegalDays ?? '—'} days` },
  { key: 'exit_buffer_days', label: 'Exit', legalLabel: (cfg) => `legal = age 3 − ${cfg?.exitLegalDays ?? '—'} days` },
];

/** Paths & contact — address, phone, AT coordinator (no template path; that's in Azure). */
const OTHER_FIELDS = [
  { key: 'address_line1', label: 'Address line 1', placeholder: '' },
  { key: 'address_line2', label: 'Address line 2', placeholder: '' },
  { key: 'city', label: 'City', placeholder: '' },
  { key: 'state', label: 'State', placeholder: '' },
  { key: 'zip', label: 'ZIP code', placeholder: '' },
  { key: 'phone', label: 'Phone', placeholder: '' },
  { key: 'at_coordinator_name', label: 'AT coordinator display name (mail merge)', placeholder: 'e.g. Jane Smith' },
  { key: 'at_coordinator_fax', label: 'AT coordinator fax (mail merge)', placeholder: '' },
];

/** Email (SMTP / Central Availability). Password and client secret are write-only. */
const EMAIL_FIELDS = [
  { key: 'provider_email_method', label: 'Provider email method', type: 'select', options: [
    { value: 'mailto', label: 'mailto (open in email client)' },
    { value: 'smtp', label: 'smtp (send from server, username/password)' },
    { value: 'office365_graph', label: 'Office 365 (Graph API, OAuth — no Basic Auth)' },
  ]},
  { key: 'office365_tenant_id', label: 'Office 365 Tenant ID (Azure)', type: 'text', placeholder: 'e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
  { key: 'office365_client_id', label: 'Office 365 Client ID (App registration)', type: 'text', placeholder: '' },
  { key: 'office365_client_secret', label: 'Office 365 Client secret (leave blank to keep current)', type: 'password', placeholder: '', writeOnly: true },
  { key: 'smtp_server', label: 'SMTP server', type: 'text', placeholder: 'e.g. smtp.office365.com' },
  { key: 'smtp_port', label: 'SMTP port', type: 'text', placeholder: '587' },
  { key: 'smtp_secure', label: 'SMTP secure (SSL); uncheck for port 587 / STARTTLS', type: 'checkbox' },
  { key: 'smtp_username', label: 'SMTP username', type: 'text', placeholder: 'e.g. Availability-CFC4@dayonepact.org' },
  { key: 'smtp_password', label: 'SMTP password (leave blank to keep current)', type: 'password', placeholder: '', writeOnly: true },
  { key: 'central_availability_from_address', label: 'Central Availability From (address)', type: 'text', placeholder: 'e.g. Availability-CFC4@dayonepact.org' },
  { key: 'central_availability_from_name', label: 'Central Availability From (name)', type: 'text', placeholder: 'e.g. Availability-CFC4' },
  { key: 'email_from_address', label: 'Default From address (fallback)', type: 'text', placeholder: '' },
  { key: 'email_from_name', label: 'Default From name (fallback)', type: 'text', placeholder: '' },
];

function toFormValues(settings) {
  const out = {};
  BUFFER_ROWS.forEach(({ key }) => {
    const v = settings?.[key];
    out[key] = v != null && v !== '' ? String(v) : '';
  });
  OTHER_FIELDS.forEach(({ key }) => {
    const v = settings?.[key];
    out[key] = v != null && v !== '' ? String(v) : '';
  });
  out.recredential_notice_details = settings?.recredential_notice_details ?? '';
  out.eom_checklist_notice_details = settings?.eom_checklist_notice_details ?? '';
  EMAIL_FIELDS.forEach(({ key, writeOnly, type }) => {
    if (writeOnly) {
      out[key] = '';
      return;
    }
    if (type === 'checkbox') {
      out[key] = settings?.[key] !== false && settings?.[key] !== undefined ? true : false;
      return;
    }
    const v = settings?.[key];
    out[key] = v != null && v !== '' ? String(v) : '';
  });
  const acs = settings?.at_coordinator_staff_id;
  out.at_coordinator_staff_id =
    acs != null && acs !== '' ? String(acs) : '';
  const dgn = settings?.dgn_coordinator_staff_id;
  out.dgn_coordinator_staff_id = dgn != null && dgn !== '' ? String(dgn) : '';
  return out;
}

function TenantSettings() {
  const { tenantPath } = useTenantFromUrl();
  const [data, setData] = useState({ settings: null, effective_milestone_config: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [documentsOnAssign, setDocumentsOnAssign] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [folderOptions, setFolderOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [atCoordinatorStaffOptions, setAtCoordinatorStaffOptions] = useState([]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setSaveMessage(null);
      const res = await tenantSettingsAPI.get();
      const d = res.data.data || {};
      setData(d);
      setFormValues(toFormValues(d.settings));
      const raw = d.settings?.documents_on_assign;
      const list = Array.isArray(raw) ? raw : [];
      setDocumentsOnAssign(list.map((item) => ({
        template_id: item.template_id ?? item.templateId ?? '',
        folder_key: item.folder_key ?? item.folderKey ?? '',
      })));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load tenant settings');
      setData({});
      setFormValues(toFormValues(null));
      setDocumentsOnAssign([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      try {
        const [tRes, ftRes] = await Promise.all([
          templateAPI.getAll().catch(() => ({ data: { templates: [] } })),
          clientDocumentAPI.getTemplate().catch(() => ({ data: { data: null } })),
        ]);
        if (cancelled) return;
        const templates = tRes.data?.templates ?? tRes.data?.data?.templates ?? [];
        setTemplateOptions((templates || []).map((t) => ({ id: String(t.id), label: t.displayName || t.display_name || `Template ${t.id}` })));
        const template = ftRes.data?.data;
        const folders = template?.template_structure?.folders ?? [];
        setFolderOptions(flattenFolderOptions(folders));
      } catch (_) {
        if (!cancelled) {
          setTemplateOptions([]);
          setFolderOptions([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loading]);

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    staffAPI
      .getAll({ active: 'true' })
      .then((res) => {
        if (cancelled) return;
        const rows = res.data?.data ?? [];
        const list = Array.isArray(rows) ? [...rows] : [];
        list.sort((a, b) => {
          const na = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
          const nb = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
          return na.localeCompare(nb);
        });
        setAtCoordinatorStaffOptions(list);
      })
      .catch(() => {
        if (!cancelled) setAtCoordinatorStaffOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [loading]);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setSaveTrigger((p) => p + 1);
  };

  const setDocumentsOnAssignRow = (index, field, value) => {
    setDocumentsOnAssign((prev) => {
      const next = [...(prev || [])];
      if (!next[index]) next[index] = { template_id: '', folder_key: '' };
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setSaveTrigger((p) => p + 1);
  };
  const addDocumentsOnAssignRow = () => {
    setDocumentsOnAssign((prev) => [...(prev || []), { template_id: '', folder_key: '' }]);
    setSaveTrigger((p) => p + 1);
  };
  const removeDocumentsOnAssignRow = (index) => {
    setDocumentsOnAssign((prev) => (prev || []).filter((_, i) => i !== index));
    setSaveTrigger((p) => p + 1);
  };

  const performSave = useCallback(async () => {
    try {
      setSaving(true);
      setSaveMessage(null);
      const payload = {};
      BUFFER_ROWS.forEach(({ key }) => {
        const v = formValues[key];
        if (v !== '' && v != null) {
          const n = parseInt(v, 10);
          payload[key] = Number.isNaN(n) ? null : n;
        } else {
          payload[key] = null;
        }
      });
      OTHER_FIELDS.forEach(({ key }) => {
        const v = formValues[key];
        payload[key] = v === '' || v == null ? null : String(v).trim() || null;
      });
      {
        const v = formValues.recredential_notice_details;
        payload.recredential_notice_details = v === '' || v == null ? null : String(v).trim() || null;
      }
      {
        const v = formValues.eom_checklist_notice_details;
        payload.eom_checklist_notice_details = v === '' || v == null ? null : String(v).trim() || null;
      }
      {
        const v = formValues.at_coordinator_staff_id;
        if (v === '' || v == null) {
          payload.at_coordinator_staff_id = null;
        } else {
          const n = parseInt(String(v), 10);
          payload.at_coordinator_staff_id = Number.isNaN(n) ? null : n;
        }
      }
      {
        const v = formValues.dgn_coordinator_staff_id;
        if (v === '' || v == null) {
          payload.dgn_coordinator_staff_id = null;
        } else {
          const n = parseInt(String(v), 10);
          payload.dgn_coordinator_staff_id = Number.isNaN(n) ? null : n;
        }
      }
      EMAIL_FIELDS.forEach(({ key, writeOnly, type }) => {
        if (writeOnly && key === 'smtp_password') {
          if (formValues[key] != null && String(formValues[key]).trim() !== '') {
            payload[key] = String(formValues[key]).trim();
          }
          return;
        }
        if (type === 'checkbox') {
          payload[key] = formValues[key] === true;
          return;
        }
        const v = formValues[key];
        payload[key] = v === '' || v == null ? null : String(v).trim() || null;
      });
      const docsList = (documentsOnAssign || []).filter(
        (row) => (row.template_id != null && String(row.template_id).trim() !== '') && (row.folder_key != null && String(row.folder_key).trim() !== '')
      ).map((row) => ({
        template_id: String(row.template_id).trim(),
        folder_key: String(row.folder_key).trim(),
      }));
      payload.documents_on_assign = docsList;
      await tenantSettingsAPI.update(payload);
      setSaveMessage('Saved.');
      // Do not call load() here: it overwrites formValues with the server response and can
      // reset fields the user is still typing in (e.g. address_line1) if the response
      // shape or timing differs, causing flaky input behavior.
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [formValues, documentsOnAssign]);

  useAutoSave(saveTrigger, performSave, { enabled: true, delayMs: 500 });

  if (loading) return <div className="tenant-settings-page"><p>Loading tenant settings...</p></div>;
  if (error) return <div className="tenant-settings-page"><p className="error-msg">{error}</p></div>;

  const { settings, effective_milestone_config: cfg } = data;
  const hasStored = settings && settings.tenant_id != null;

  return (
    <div className="tenant-settings-page">
      <ContentHeader
        title="Tenant Settings"
        backTo={tenantPath('/settings')}
        backLabel="Back to Settings"
        titleHint={<>Tracking buffer days, paths, and contact info are stored per tenant and loaded <strong>once per request</strong>—no extra round-trip.</>}
        actions={
          saveMessage && (
            <span className={saveMessage.startsWith('Saved') ? 'tenant-settings-status' : 'tenant-settings-status error'}>
              {saveMessage}
            </span>
          )
        }
      />

      <section className="tenant-settings-section tenant-settings-edit">
        <h2>Edit tracking buffer days</h2>
        <p className="stored-hint">
          Legal values are set by regulation and cannot be changed. You can only set <strong>buffer days</strong> (e.g. 1 day before the legal date). Leave a buffer blank to use the app default.
        </p>
        {hasStored ? (
          <p className="stored-hint secondary">This tenant has custom values in the database.</p>
        ) : (
          <p className="stored-hint secondary">No row yet for this tenant—enter values and they will be saved automatically.</p>
        )}
        <div className="tenant-settings-form buffer-rows">
          {BUFFER_ROWS.map(({ key, label, legalLabel }) => (
            <div key={key} className="buffer-row">
              <span className="buffer-label">{label}:</span>
              <span className="buffer-legal">{legalLabel(cfg)}</span>
              <span className="buffer-sep">·</span>
              <label htmlFor={key} className="buffer-input-label">buffer</label>
              <input
                id={key}
                type="text"
                inputMode="numeric"
                placeholder="default"
                value={formValues[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                className="form-input buffer-input"
              />
              <span className="buffer-unit">day(s)</span>
            </div>
          ))}
        </div>
      </section>

      <section className="tenant-settings-section tenant-settings-other">
        <h2>Address & contact</h2>
        <p className="stored-hint">
          Address and phone. AT coordinator staff receives the internal inbox item when an AT packet moves to <strong>Submitted ATC</strong>. Diagnostic (DGN) coordinator receives an inbox item when a new <strong>DGN</strong> service need is created. Display name and fax are still used for document mail merge.
        </p>
        <div className="tenant-settings-form other-fields">
          {OTHER_FIELDS.filter(({ key }) => key !== 'at_coordinator_name' && key !== 'at_coordinator_fax').map(({ key, label, placeholder }) => (
            <div key={key} className="other-row">
              <label htmlFor={key}>{label}</label>
              <input
                id={key}
                type="text"
                placeholder={placeholder || ''}
                value={formValues[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                className="form-input other-input"
              />
            </div>
          ))}
          <div className="other-row">
            <label htmlFor="at_coordinator_staff_id">AT coordinator staff (Submitted ATC inbox)</label>
            <select
              id="at_coordinator_staff_id"
              value={formValues.at_coordinator_staff_id ?? ''}
              onChange={(e) => handleChange('at_coordinator_staff_id', e.target.value)}
              className="form-input other-input"
            >
              <option value="">None — Submitted ATC inbox notifications will be skipped</option>
              {atCoordinatorStaffOptions.map((s) => {
                const id = s.id != null ? String(s.id) : '';
                const nm = [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.email || `Staff ${id}`;
                return (
                  <option key={id} value={id}>{nm}</option>
                );
              })}
            </select>
          </div>
          <div className="other-row">
            <label htmlFor="dgn_coordinator_staff_id">Diagnostic (DGN) coordinator staff (new DGN need inbox)</label>
            <select
              id="dgn_coordinator_staff_id"
              value={formValues.dgn_coordinator_staff_id ?? ''}
              onChange={(e) => handleChange('dgn_coordinator_staff_id', e.target.value)}
              className="form-input other-input"
            >
              <option value="">None — new DGN need inbox notifications will be skipped</option>
              {atCoordinatorStaffOptions.map((s) => {
                const id = s.id != null ? String(s.id) : '';
                const nm = [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.email || `Staff ${id}`;
                return (
                  <option key={`dgn-${id}`} value={id}>{nm}</option>
                );
              })}
            </select>
          </div>
          {OTHER_FIELDS.filter(({ key }) => key === 'at_coordinator_name' || key === 'at_coordinator_fax').map(({ key, label, placeholder }) => (
            <div key={key} className="other-row">
              <label htmlFor={key}>{label}</label>
              <input
                id={key}
                type="text"
                placeholder={placeholder || ''}
                value={formValues[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                className="form-input other-input"
              />
            </div>
          ))}
        </div>
      </section>


      <section className="tenant-settings-section tenant-settings-email">
        <h2>Email Settings</h2>
        <p className="stored-hint">
          <strong>Provider email method</strong> controls how Provider Team emails (from the Provider Team tab) and AT Packet external notifications are sent.
          Set to <em>mailto</em> to open in the user&apos;s email client with recipients and body pre-filled (recommended).
          Central Availability blast emails use the &quot;Open in email&quot; approach separately and are not affected by this setting.
        </p>
        <div className="tenant-settings-form email-fields">
          {EMAIL_FIELDS.map(({ key, label, type, placeholder, options }) => (
            <div key={key} className="email-row">
              <label htmlFor={key}>{label}</label>
              {type === 'select' ? (
                <select
                  id={key}
                  value={formValues[key] ?? ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="form-input email-input"
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : type === 'checkbox' ? (
                <input
                  id={key}
                  type="checkbox"
                  checked={formValues[key] === true}
                  onChange={(e) => handleChange(key, e.target.checked)}
                  className="form-checkbox"
                />
              ) : (
                <input
                  id={key}
                  type={type || 'text'}
                  placeholder={placeholder || ''}
                  value={formValues[key] ?? ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="form-input email-input"
                  autoComplete={type === 'password' ? 'new-password' : 'off'}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="tenant-settings-section tenant-settings-docs-on-assign">
        <h2>Documents on assign</h2>
        <p className="stored-hint">
          When a client is assigned to a service coordinator, these documents are generated and added to the client&apos;s folder. Pick a template and the folder key (must match a folder in your Client Folder Template).
        </p>
        <div className="docs-on-assign-rows">
          {(documentsOnAssign || []).map((row, index) => (
            <div key={index} className="docs-on-assign-row">
              <label className="docs-on-assign-label">Template</label>
              {templateOptions.length > 0 ? (
                <select
                  value={row.template_id ?? ''}
                  onChange={(e) => setDocumentsOnAssignRow(index, 'template_id', e.target.value)}
                  className="form-input docs-on-assign-select"
                >
                  <option value="">Select template</option>
                  {templateOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="Template ID"
                  value={row.template_id ?? ''}
                  onChange={(e) => setDocumentsOnAssignRow(index, 'template_id', e.target.value)}
                  className="form-input docs-on-assign-input"
                />
              )}
              <label className="docs-on-assign-label">Folder</label>
              {folderOptions.length > 0 ? (
                <select
                  value={row.folder_key ?? ''}
                  onChange={(e) => setDocumentsOnAssignRow(index, 'folder_key', e.target.value)}
                  className="form-input docs-on-assign-select"
                >
                  <option value="">Select folder</option>
                  {folderOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="Folder key (e.g. Intake)"
                  value={row.folder_key ?? ''}
                  onChange={(e) => setDocumentsOnAssignRow(index, 'folder_key', e.target.value)}
                  className="form-input docs-on-assign-input"
                />
              )}
              <button
                type="button"
                onClick={() => removeDocumentsOnAssignRow(index)}
                className="docs-on-assign-remove"
                aria-label="Remove row"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addDocumentsOnAssignRow} className="docs-on-assign-add">
          Add document
        </button>
      </section>

      <div className="tenant-settings-links">
        <Link to={tenantPath('/dashboard/rules')} className="link-rules">View referral tracking rules (in English)</Link>
      </div>
    </div>
  );
}

export default TenantSettings;
